/**
 * vore-pdp.js — comportamento das secoes de PDP template A da VORE.
 *
 * Reimplementa, sem jQuery, sem Swiper e sem OverlayScrollbars, os cinco
 * scripts inline que o site original injeta em cada PDP
 * (raw/products/vore-sleep-patches.html):
 *
 *   <vore-pdp-buy-box>       `subscription_box_cHYBCK` — 3 tiers de pack, toggle
 *                       Subscribe/One-time, precos calculados no cliente a
 *                       partir de [data-sb-config]. Cada tier pode apontar
 *                       para uma variante real (`variantId`) ou, sem ela,
 *                       cair no modo antigo de multiplicar a quantidade.
 *   <vore-osv>        `our_survey` — contador animado + barra de progresso.
 *   <vore-wsb>      `benefit_section` — trilho da timeline preenchido no scroll.
 *   <vore-pdp-carousel>      `ws_new_ingredients` / `wixkraft_ugc` — trilho com
 *                       scroll-snap, setas e dots (um dot por parada real).
 *   <vore-wkugc-modal>     modal de reels do `wixkraft_ugc`.
 *   <vore-pdp-gallery>       galeria do `main` — slider mobile, contador, lightbox.
 *   <vore-uc>        cross-sell `Tambem da VORE` — add to cart AJAX.
 *
 * Matematica de preco, com o tier ligado a uma variante (o caso da VORE BR):
 *   total        = preco da variante do tier (ou o preco do selling plan dela)
 *   compareTotal = compare_at_price da variante do tier
 *   perPack      = total / tier.packs
 *   savePercent  = round((compareTotal - total) / compareTotal * 100)
 *
 * Sem variante ligada vale a conta do site original:
 *   total        = unitPrice(mode) * tier.paidQty   (as unidades gratis nao sao
 *                  cobradas aqui; quem concede e o desconto automatico da loja)
 *   compareTotal = compareAtPrice * tier.totalQty
 *   perPack      = total / totalQty
 *
 * O add to cart usa @theme/events (CartAddEvent) quando disponivel, para que o
 * cart drawer do tema Kind abra e faca o morph sozinho; se o modulo nao existir,
 * cai para uma navegacao normal ate /cart.
 */

/* ------------------------------------------------------------------ helpers */

const prefersReducedMotion = () =>
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Formata centavos como moeda. Valores inteiros perdem os decimais
 * (30.00 -> €30), o resto mantem os dois (18.95 -> €18,95) — igual ao original.
 * @param {number} cents
 * @param {string} currency
 * @param {string} locale
 * @returns {string}
 */
function formatMoney(cents, currency, locale) {
  const value = Math.round(Number(cents) || 0);
  const fraction = value % 100 === 0 ? 0 : 2;

  try {
    return new Intl.NumberFormat(locale || 'pt-BR', {
      style: 'currency',
      currency: currency || 'BRL',
      minimumFractionDigits: fraction,
      maximumFractionDigits: fraction,
    }).format(value / 100);
  } catch (error) {
    return `${(value / 100).toFixed(fraction)} ${currency || ''}`.trim();
  }
}

/**
 * POST /cart/add.js e avisa o cart drawer do tema.
 * @param {object} item
 * @param {HTMLElement} host
 * @returns {Promise<object>}
 */
async function addItemToCart(item, host) {
  const root = window.Shopify?.routes?.root ?? '/';
  const addUrl = window.KindTheme?.routes?.cart_add_url || `${root}cart/add.js`;

  /** @type {string[]} */
  let sections = [];
  let events = null;

  try {
    events = await import('@theme/events');
    document.dispatchEvent(new events.CartGroupedSections(sections));
    sections = [...new Set(sections)];
  } catch (error) {
    events = null;
  }

  const body = { items: [item] };
  if (sections.length > 0) body.sections = sections.join(',');

  const response = await fetch(addUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });

  const data = await response.json();
  if (!response.ok || data.status) {
    throw new Error(data.description || data.message || 'Nao foi possivel adicionar ao carrinho.');
  }

  if (events) {
    host.dispatchEvent(
      new events.CartAddEvent(data, String(item.id), {
        source: 'vore-pdp',
        itemCount: item.quantity,
        variantId: String(item.id),
        sections: data.sections,
      })
    );
  } else {
    window.location.href = `${root}cart`;
  }

  return data;
}

/* -------------------------------------------------------------- <vore-pdp-buy-box> */

class RstPdpBuyBox extends HTMLElement {
  connectedCallback() {
    if (this.dataset.voreInit === 'true') return;
    this.dataset.voreInit = 'true';

    const configNode = this.querySelector('[data-sb-config]');
    if (!configNode) return;

    try {
      this.config = JSON.parse(configNode.textContent);
    } catch (error) {
      console.error('vore-pdp-buy-box: data-sb-config invalido', error);
      return;
    }

    const cfg = this.config;

    cfg.tiers = (cfg.tiers || []).map((tier) => {
      const paid = Number(tier.paidQty) || 0;
      const free = Number(tier.freeQty) || 0;
      const variantId = Number(tier.variantId) || 0;
      const packs = Number(tier.packs) || 0;
      return {
        ...tier,
        paidQty: paid,
        freeQty: free,
        totalQty: paid + free,
        variantId,
        packs: packs > 0 ? packs : Math.max(1, paid + free),
        price: Number(tier.price) || 0,
        compareAtPrice: Number(tier.compareAtPrice) || 0,
        available: tier.available !== false,
        sellingPlanId: Number(tier.sellingPlanId) || 0,
        sellingPlanPrice: Number(tier.sellingPlanPrice) || 0,
      };
    });

    if (cfg.tiers.length === 0) {
      cfg.tiers = [
        {
          title: '',
          label: '',
          paidQty: 1,
          freeQty: 0,
          totalQty: 1,
          packs: 1,
          variantId: 0,
          price: 0,
          compareAtPrice: 0,
          available: true,
          sellingPlanId: 0,
          sellingPlanPrice: 0,
        },
      ];
    }

    cfg.texts = cfg.texts || {};

    this.currency = this.dataset.currency || 'BRL';
    this.locale = this.dataset.locale || 'pt-BR';

    const defaultIndex = parseInt(cfg.defaultTierIndex, 10);
    let startIndex = Number.isNaN(defaultIndex)
      ? 0
      : Math.max(0, Math.min(defaultIndex, cfg.tiers.length - 1));

    // Quando a URL pede uma variante (?variant=...), o tier dela e o que abre.
    const selectedVariantId = Number(cfg.selectedVariantId) || 0;
    if (selectedVariantId) {
      const selectedTier = cfg.tiers.findIndex((tier) => tier.variantId === selectedVariantId);
      if (selectedTier >= 0) startIndex = selectedTier;
    }

    this.state = {
      tierIndex: startIndex,
      mode: cfg.defaultMode === 'one_time' ? 'one_time' : 'subscribe',
      isAdding: false,
    };

    this.els = {
      headingLabel: this.querySelector('[data-sb-heading-label]'),
      tiers: [...this.querySelectorAll('[data-sb-tier]')],
      plans: [...this.querySelectorAll('[data-sb-mode]')],
      subPrice: this.querySelector('[data-sb-sub-price]'),
      subCompare: this.querySelector('[data-sb-sub-compare]'),
      subPerPack: this.querySelector('[data-sb-sub-perpack]'),
      saveBadge: this.querySelector('[data-sb-save-badge]'),
      otPrice: this.querySelector('[data-sb-ot-price]'),
      otCompare: this.querySelector('[data-sb-ot-compare]'),
      otPerPack: this.querySelector('[data-sb-ot-perpack]'),
      atc: this.querySelector('[data-sb-atc]'),
      atcText: this.querySelector('[data-sb-atc-text]'),
      error: this.querySelector('[data-sb-error]'),
    };

    this.els.tiers.forEach((button) => {
      button.addEventListener('click', () => {
        this.state.tierIndex = Number(button.dataset.sbTier) || 0;
        this.render();
      });
    });

    this.els.plans.forEach((card) => {
      const pick = () => {
        this.state.mode = card.dataset.sbMode === 'one_time' ? 'one_time' : 'subscribe';
        this.render();
      };

      card.addEventListener('click', pick);
      card.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          pick();
        }
      });
    });

    this.els.atc?.addEventListener('click', () => this.addToCart());

    this.render();
  }

  get activeTier() {
    return this.config.tiers[this.state.tierIndex] || this.config.tiers[0];
  }

  /** Variante que o tier ativo manda para o carrinho. */
  get activeVariantId() {
    return this.activeTier.variantId || Number(this.config.variantId) || 0;
  }

  /** Disponibilidade do tier ativo — a da variante quando ha uma ligada. */
  get isAvailable() {
    const tier = this.activeTier;
    if (tier.variantId) return tier.available;
    return Boolean(this.config.available);
  }

  /**
   * @param {string} mode
   * @param {object} tier
   */
  unitPrice(mode, tier) {
    const cfg = this.config;
    const base = tier.variantId ? tier.price : Number(cfg.price) || 0;

    if (mode === 'subscribe') {
      const planPrice = tier.variantId ? tier.sellingPlanPrice : Number(cfg.sellingPlanPrice) || 0;
      if (planPrice) return planPrice;
      const percent = Number(cfg.subDiscountPercent) || 0;
      return Math.round(base * (1 - percent / 100));
    }

    return base;
  }

  /** @param {string} mode */
  pricing(mode) {
    const tier = this.activeTier;
    const linked = Boolean(tier.variantId);
    const unit = this.unitPrice(mode, tier);

    const total = linked ? unit : unit * tier.paidQty;

    const compareUnit = linked
      ? tier.compareAtPrice || tier.price
      : Number(this.config.compareAtPrice) || Number(this.config.price) || 0;
    const compareTotal = linked ? compareUnit : compareUnit * tier.totalQty;

    const packs = Math.max(1, linked ? tier.packs : tier.totalQty);

    return {
      total,
      compareTotal,
      perPack: total / packs,
      savePercent:
        compareTotal > 0 ? Math.round(((compareTotal - total) / compareTotal) * 100) : 0,
    };
  }

  /**
   * @param {Element|null} priceEl
   * @param {Element|null} compareEl
   * @param {Element|null} perPackEl
   * @param {{total:number, compareTotal:number, perPack:number}} pricing
   */
  renderPriceColumn(priceEl, compareEl, perPackEl, pricing) {
    const cfg = this.config;
    const money = (cents) => formatMoney(cents, this.currency, this.locale);

    if (priceEl) priceEl.textContent = money(pricing.total);

    if (compareEl) {
      const show = cfg.showCompare && pricing.compareTotal > pricing.total;
      compareEl.textContent = show ? money(pricing.compareTotal) : '';
      compareEl.hidden = !show;
    }

    if (perPackEl) {
      perPackEl.textContent = cfg.showPerPack
        ? money(pricing.perPack) + (cfg.texts.perPack || '')
        : '';
    }
  }

  /** @param {{total:number, compareTotal:number}} pricing */
  syncTitlePrice(pricing) {
    if (!this.config.syncTitlePrice) return;

    const scope = this.closest('[data-vore-pdp-info]') || document;
    const money = (cents) => formatMoney(cents, this.currency, this.locale);

    scope.querySelectorAll('[data-vore-price-sale]').forEach((node) => {
      node.textContent = money(pricing.total);
    });

    const showCompare = pricing.compareTotal > pricing.total;

    scope.querySelectorAll('[data-vore-price-compare]').forEach((node) => {
      node.textContent = showCompare ? money(pricing.compareTotal) : '';
      node.hidden = !showCompare;
    });

    scope.querySelectorAll('[data-vore-price-compare-label]').forEach((node) => {
      node.hidden = !showCompare;
    });
  }

  render() {
    const cfg = this.config;

    this.els.tiers.forEach((button) => {
      const active = Number(button.dataset.sbTier) === this.state.tierIndex;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-checked', active ? 'true' : 'false');
    });

    if (this.els.headingLabel) {
      this.els.headingLabel.textContent = this.activeTier.label || '';
    }

    this.els.plans.forEach((card) => {
      const active = card.dataset.sbMode === this.state.mode;
      card.classList.toggle('is-active', active);
      card.setAttribute('aria-checked', active ? 'true' : 'false');
    });

    const subscribe = this.pricing('subscribe');
    const oneTime = this.pricing('one_time');

    this.renderPriceColumn(this.els.subPrice, this.els.subCompare, this.els.subPerPack, subscribe);
    this.renderPriceColumn(this.els.otPrice, this.els.otCompare, this.els.otPerPack, oneTime);

    if (this.els.saveBadge && cfg.autoSaveBadge) {
      const show = subscribe.savePercent > 0;
      this.els.saveBadge.textContent = show
        ? `${cfg.texts.savePrefix || 'Economize'} ${subscribe.savePercent}%`
        : '';
      this.els.saveBadge.hidden = !show;
    }

    this.syncTitlePrice(this.state.mode === 'subscribe' ? subscribe : oneTime);

    if (this.els.atc) {
      if (!this.isAvailable) {
        this.els.atc.disabled = true;
        if (this.els.atcText) this.els.atcText.textContent = cfg.texts.soldOut || 'Esgotado';
      } else {
        this.els.atc.disabled = this.state.isAdding;
        if (this.els.atcText) {
          this.els.atcText.textContent = this.state.isAdding
            ? cfg.texts.adding || 'Adicionando...'
            : cfg.texts.atc || 'Adicionar ao carrinho';
        }
      }
    }
  }

  /** @param {string} message */
  showError(message) {
    if (!this.els.error) return;
    this.els.error.textContent = message;
    this.els.error.hidden = false;
  }

  clearError() {
    if (!this.els.error) return;
    this.els.error.hidden = true;
    this.els.error.textContent = '';
  }

  async addToCart() {
    const cfg = this.config;
    const tier = this.activeTier;
    const variantId = this.activeVariantId;

    if (this.state.isAdding || !this.isAvailable || !variantId) return;

    const item = {
      id: variantId,
      quantity: tier.variantId ? 1 : tier.totalQty,
      properties: {
        _vore_tier: tier.title,
        _vore_mode: this.state.mode,
      },
    };

    const sellingPlanId = tier.variantId
      ? tier.sellingPlanId
      : Number(cfg.sellingPlanId) || 0;

    if (this.state.mode === 'subscribe' && sellingPlanId) {
      item.selling_plan = sellingPlanId;
    }

    this.state.isAdding = true;
    this.clearError();
    this.render();

    try {
      await addItemToCart(item, this);
    } catch (error) {
      this.showError(error.message || cfg.texts.error || 'Tente novamente');
    } finally {
      this.state.isAdding = false;
      this.render();
    }
  }
}

/* --------------------------------------------------------------- <vore-osv> */

class RstOsv extends HTMLElement {
  connectedCallback() {
    if (this.dataset.voreInit === 'true') return;
    this.dataset.voreInit = 'true';

    const bars = [...this.querySelectorAll('[data-progress-bar]')];
    const numbers = [...this.querySelectorAll('[data-animate-number]')].map((el) => {
      const match = el.textContent.trim().match(/^([^\d]*)(\d+(?:[.,]\d+)?)(.*)$/);
      return {
        el,
        value: match ? parseFloat(match[2].replace(',', '.')) : null,
        prefix: match ? match[1] : '',
        suffix: match ? match[3] : '',
      };
    });

    bars.forEach((bar, index) => {
      const value = numbers[index] ? numbers[index].value : null;
      bar.dataset.target = value === null ? '0' : String(Math.max(0, Math.min(100, value)));
    });

    const paint = (progress) => {
      bars.forEach((bar) => {
        bar.style.width = `${Number(bar.dataset.target) * progress}%`;
      });
      numbers.forEach((part) => {
        if (part.value === null) return;
        part.el.textContent = part.prefix + Math.round(part.value * progress) + part.suffix;
      });
    };

    // O markup ja traz os valores reais, entao sem JS a secao continua correta.
    if (prefersReducedMotion()) {
      paint(1);
      return;
    }

    paint(0);

    let done = false;
    const run = () => {
      if (done) return;
      done = true;

      bars.forEach((bar) => {
        bar.style.width = `${bar.dataset.target}%`;
      });

      let start = null;
      const step = (now) => {
        if (start === null) start = now;
        const progress = Math.min((now - start) / 1200, 1);
        const eased = 1 - (1 - progress) ** 3;
        numbers.forEach((part) => {
          if (part.value === null) return;
          part.el.textContent = part.prefix + Math.round(part.value * eased) + part.suffix;
        });
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    if (!('IntersectionObserver' in window)) {
      run();
      return;
    }

    // Uma secao alta nunca atinge um threshold alto no mobile: dispare no primeiro pixel.
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            run();
            observer.disconnect();
          }
        });
      },
      { threshold: 0, rootMargin: '0px 0px -15% 0px' }
    );

    observer.observe(this);
  }
}

/* ------------------------------------------------------------- <vore-wsb> */

class RstWsb extends HTMLElement {
  connectedCallback() {
    if (this.dataset.voreInit === 'true') return;
    this.dataset.voreInit = 'true';

    const list = this.querySelector('[data-benefits-steps]');
    const track = this.querySelector('[data-benefits-track]');
    const fill = this.querySelector('[data-benefits-progress]');
    const steps = [...this.querySelectorAll('[data-benefits-step]')];

    if (!list || !track || !fill || steps.length === 0) return;

    // Centro do ponto dentro do passo: top 8px + metade dos 7px do ponto (igual ao CSS).
    const DOT_CENTER = 11.5;
    let trackTop = 0;
    let trackHeight = 0;

    const dotOffset = (step) => step.offsetTop + DOT_CENTER;

    const layout = () => {
      trackTop = dotOffset(steps[0]);
      trackHeight = Math.max(0, dotOffset(steps[steps.length - 1]) - trackTop);
      track.style.top = `${trackTop}px`;
      track.style.height = `${trackHeight}px`;
    };

    if (prefersReducedMotion()) {
      layout();
      steps.forEach((step) => step.classList.add('is-active'));
      fill.style.height = `${trackHeight}px`;
      return;
    }

    let ticking = false;

    const update = () => {
      ticking = false;
      layout();

      const trigger = window.innerHeight * 0.7;
      let filled = 0;

      steps.forEach((step) => {
        const rect = step.getBoundingClientRect();
        const dotTop = rect.top + DOT_CENTER;

        if (dotTop < trigger && rect.bottom > 0) {
          step.classList.add('is-active');
          filled = Math.max(filled, dotOffset(step) - trackTop);
        } else {
          step.classList.remove('is-active');
        }
      });

      fill.style.height = `${Math.min(filled, trackHeight)}px`;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    requestAnimationFrame(update);
  }
}

/* ------------------------------------------------------------- <vore-pdp-carousel> */

/**
 * Trilho com scroll-snap + setas + dots. Um dot por parada real de scroll,
 * nao por card: os cards que sobram no fim compartilham a ultima parada.
 */
class RstPdpCarousel extends HTMLElement {
  connectedCallback() {
    if (this.dataset.voreInit === 'true') return;
    this.dataset.voreInit = 'true';

    this.track = this.querySelector('[data-carousel-track]');
    if (!this.track) return;

    this.nav = this.querySelector('[data-carousel-nav]');
    this.dotsWrap = this.querySelector('[data-carousel-dots]');
    this.prevButton = this.querySelector('[data-carousel-prev]');
    this.nextButton = this.querySelector('[data-carousel-next]');
    this.stops = [];
    this.dots = [];

    this.prevButton?.addEventListener('click', () => this.page(-1));
    this.nextButton?.addEventListener('click', () => this.page(1));
    this.track.addEventListener('scroll', () => this.refresh(), { passive: true });

    this.resizeObserver = new ResizeObserver(() => this.build());
    this.resizeObserver.observe(this.track);

    this.build();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  get step() {
    const slide = this.track.querySelector('[data-carousel-slide]');
    if (!slide) return this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap || '0') || 0;
    return slide.getBoundingClientRect().width + gap;
  }

  get scrollable() {
    return this.track.scrollWidth - this.track.clientWidth > 1;
  }

  build() {
    const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    const step = this.step || 1;
    const count = Math.max(1, Math.ceil(max / step) + 1);

    this.stops = Array.from({ length: count }, (_, index) => Math.min(index * step, max));

    if (this.dotsWrap) {
      this.dotsWrap.textContent = '';
      this.dots = this.stops.map((position, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'vore-wkugc__dot';
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', `${index + 1}`);
        dot.addEventListener('click', () => {
          this.track.scrollTo({ left: position, behavior: 'smooth' });
        });
        this.dotsWrap.appendChild(dot);
        return dot;
      });
    }

    this.refresh();
  }

  /** @param {number} direction */
  page(direction) {
    this.track.scrollBy({ left: this.step * direction, behavior: 'smooth' });
  }

  refresh() {
    const scrollable = this.scrollable;

    if (this.nav) this.nav.hidden = !scrollable;
    if (this.prevButton) this.prevButton.disabled = !scrollable || this.track.scrollLeft <= 1;
    if (this.nextButton) {
      this.nextButton.disabled =
        !scrollable ||
        this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 1;
    }

    if (this.dots.length === 0) return;

    let closest = 0;
    let distance = Infinity;
    this.stops.forEach((position, index) => {
      const delta = Math.abs(position - this.track.scrollLeft);
      if (delta < distance) {
        distance = delta;
        closest = index;
      }
    });

    this.dots.forEach((dot, index) => {
      dot.setAttribute('aria-selected', index === closest ? 'true' : 'false');
    });
  }
}

/* ---------------------------------------------------------- <vore-wsi> */

/** Painel sobreposto de cada card de ingrediente. */
class RstWsi extends HTMLElement {
  connectedCallback() {
    if (this.dataset.voreInit === 'true') return;
    this.dataset.voreInit = 'true';

    this.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-ingredient-toggle]');
      const close = event.target.closest('[data-ingredient-close]');
      if (!toggle && !close) return;

      const card = (toggle || close).closest('[data-ingredient-card]');
      if (!card) return;

      const open = Boolean(toggle) && !card.classList.contains('is-open');

      this.querySelectorAll('[data-ingredient-card].is-open').forEach((other) => {
        other.classList.remove('is-open');
        other.querySelector('[data-ingredient-toggle]')?.setAttribute('aria-expanded', 'false');
      });

      card.classList.toggle('is-open', open);
      card.querySelector('[data-ingredient-toggle]')?.setAttribute(
        'aria-expanded',
        open ? 'true' : 'false'
      );
    });
  }
}

/* ------------------------------------------------------------ <vore-wkugc-modal> */

class RstWkUgcModal extends HTMLElement {
  connectedCallback() {
    if (this.dataset.voreInit === 'true') return;
    this.dataset.voreInit = 'true';

    this.video = this.querySelector('[data-ugc-video]');
    this.progress = this.querySelector('[data-ugc-progress]');
    this.sources = [];
    this.index = 0;

    const sectionId = this.dataset.sectionId;
    this.opener = document.querySelector(`[data-ugc-carousel="${sectionId}"]`);

    if (this.opener) {
      this.sources = [...this.opener.querySelectorAll('[data-ugc-open]')].map((button) => ({
        src: button.dataset.ugcSrc || '',
        poster: button.dataset.ugcPoster || '',
      }));

      this.opener.addEventListener('click', (event) => {
        const button = event.target.closest('[data-ugc-open]');
        if (!button) return;
        this.open(Number(button.dataset.ugcOpen) || 0);
      });
    }

    this.querySelectorAll('[data-ugc-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    this.querySelector('[data-ugc-prev]')?.addEventListener('click', () => this.go(-1));
    this.querySelector('[data-ugc-next]')?.addEventListener('click', () => this.go(1));

    this.querySelector('[data-ugc-mute]')?.addEventListener('click', () => {
      if (!this.video) return;
      this.video.muted = !this.video.muted;
    });

    this.video?.addEventListener('timeupdate', () => {
      if (!this.progress || !this.video.duration) return;
      this.progress.style.width = `${(this.video.currentTime / this.video.duration) * 100}%`;
    });

    this.video?.addEventListener('ended', () => this.go(1));

    document.addEventListener('keydown', (event) => {
      if (this.dataset.open !== 'true') return;
      if (event.key === 'Escape') this.close();
      if (event.key === 'ArrowRight') this.go(1);
      if (event.key === 'ArrowLeft') this.go(-1);
    });
  }

  /** @param {number} index */
  open(index) {
    if (this.sources.length === 0) return;
    this.index = index;
    this.dataset.open = 'true';
    document.body.style.overflow = 'hidden';
    this.play();
  }

  close() {
    this.dataset.open = 'false';
    document.body.style.overflow = '';
    this.video?.pause();
  }

  /** @param {number} direction */
  go(direction) {
    if (this.sources.length === 0) return;
    this.index = (this.index + direction + this.sources.length) % this.sources.length;
    this.play();
  }

  play() {
    const source = this.sources[this.index];
    if (!this.video || !source) return;
    this.video.src = source.src;
    this.video.poster = source.poster;
    if (this.progress) this.progress.style.width = '0%';
    this.video.play().catch(() => {
      /* autoplay bloqueado: o visitante aperta play */
    });
  }
}

/* -------------------------------------------------------------- <vore-pdp-gallery> */

class RstPdpGallery extends HTMLElement {
  connectedCallback() {
    if (this.dataset.voreInit === 'true') return;
    this.dataset.voreInit = 'true';

    this.list = this.querySelector('[data-gallery-list]');
    this.current = this.querySelector('[data-gallery-current]');
    this.dialog = this.querySelector('[data-gallery-dialog]');
    this.dialogImage = this.querySelector('[data-gallery-dialog-image]');

    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.page(-1));
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.page(1));

    this.list?.addEventListener('scroll', () => this.refresh(), { passive: true });

    this.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const item = this.items[Number(thumb.dataset.galleryThumb) || 0];
        if (item && this.list) {
          this.list.scrollTo({ left: item.offsetLeft - this.list.offsetLeft, behavior: 'smooth' });
        }
        this.refresh();
      });
    });

    this.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-gallery-open]');
      if (!opener || !this.dialog || !this.dialogImage) return;
      this.dialogImage.src = opener.dataset.galleryOpen;
      this.dialogImage.alt = opener.dataset.galleryAlt || '';
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    });

    this.querySelector('[data-gallery-dialog-close]')?.addEventListener('click', () => {
      this.dialog?.close();
    });

    this.refresh();
  }

  get items() {
    return [...this.querySelectorAll('[data-gallery-item]')];
  }

  /** @param {number} direction */
  page(direction) {
    if (!this.list) return;
    const item = this.items[0];
    const width = item ? item.getBoundingClientRect().width + 10 : this.list.clientWidth;
    this.list.scrollBy({ left: width * direction, behavior: 'smooth' });
  }

  refresh() {
    if (!this.list) return;

    const items = this.items;
    let active = 0;
    let distance = Infinity;

    items.forEach((item, index) => {
      const delta = Math.abs(item.offsetLeft - this.list.offsetLeft - this.list.scrollLeft);
      if (delta < distance) {
        distance = delta;
        active = index;
      }
    });

    if (this.current) this.current.textContent = String(active + 1);

    this.querySelectorAll('[data-gallery-thumb]').forEach((thumb, index) => {
      thumb.setAttribute('aria-current', index === active ? 'true' : 'false');
    });

    const prev = this.querySelector('[data-gallery-prev]');
    const next = this.querySelector('[data-gallery-next]');
    if (prev) prev.disabled = this.list.scrollLeft <= 1;
    if (next) {
      next.disabled = this.list.scrollLeft + this.list.clientWidth >= this.list.scrollWidth - 1;
    }
  }
}

/* --------------------------------------------------------------- <vore-uc> */

class RstUc extends HTMLElement {
  connectedCallback() {
    if (this.dataset.voreInit === 'true') return;
    this.dataset.voreInit = 'true';

    this.addEventListener('click', async (event) => {
      const button = event.target.closest('[data-upsell-add]');
      if (!(button instanceof HTMLButtonElement) || button.disabled) return;

      const variantId = Number(button.dataset.variantId);
      if (!variantId) return;

      const label = button.querySelector('[data-upsell-label]') || button;
      const idle = label.textContent;

      button.disabled = true;
      label.textContent = button.dataset.addingText || 'Adicionando...';

      try {
        await addItemToCart({ id: variantId, quantity: 1 }, this);
        label.textContent = idle;
      } catch (error) {
        label.textContent = button.dataset.errorText || 'Nao foi possivel adicionar ao carrinho.';
        setTimeout(() => {
          label.textContent = idle;
        }, 2500);
      } finally {
        button.disabled = false;
      }
    });
  }
}

/* ------------------------------------------------------------------ registro */

const components = {
  'vore-pdp-buy-box': RstPdpBuyBox,
  'vore-osv': RstOsv,
  'vore-wsb': RstWsb,
  'vore-pdp-carousel': RstPdpCarousel,
  'vore-wsi': RstWsi,
  'vore-wkugc-modal': RstWkUgcModal,
  'vore-pdp-gallery': RstPdpGallery,
  'vore-uc': RstUc,
};

Object.entries(components).forEach(([name, constructor]) => {
  if (!customElements.get(name)) customElements.define(name, constructor);
});

export {
  RstPdpBuyBox,
  RstOsv,
  RstWsb,
  RstPdpCarousel,
  RstWsi,
  RstWkUgcModal,
  RstPdpGallery,
  RstUc,
};
